<template>
  <v-layout column class="mx-auto px-15 signup">
    <h5 class="mb-2 text-h5">
      Sign Up
    </h5>
    <p class="mb-6 text-subtitle-2">
      Already member? <NuxtLink to="/login" class="primary--text">
        Sign In
      </NuxtLink>
    </p>

    <v-form ref="form" @submit.prevent="signUp">
      <label class="text-subtitle-2">Email</label>
      <v-text-field
        v-model="formData.email"
        :rules="[$formRules.required, $formRules.email]"
        dense
        outlined
        placeholder="Enter your email"
        class="mt-1"
      />

      <label class="text-subtitle-2">Password</label>
      <v-hover v-slot="{ hover }">
        <v-text-field
          v-model="formData.password"
          :rules="[$formRules.required, $formRules.password]"
          :type="isShowPassword ? 'text' : 'password'"
          dense
          outlined
          placeholder="Enter your password"
          class="mt-1"
        >
          <template #append>
            <v-icon v-show="hover" tabindex="-1" @click="isShowPassword = !isShowPassword">
              {{ isShowPassword ? 'visibility' : 'visibility_off' }}
            </v-icon>
          </template>
        </v-text-field>
      </v-hover>

      <label class="text-subtitle-2">Confirm Password</label>
      <v-hover v-slot="{ hover }">
        <v-text-field
          v-model="formData.confirmPassword"
          :rules="[$formRules.required, $formRules.confirmPassword(formData.confirmPassword, formData.password)]"
          :type="isShowConfirmPassword ? 'text' : 'password'"
          dense
          outlined
          placeholder="Confirm your password"
          class="mt-1"
        >
          <template #append>
            <v-icon v-show="hover" tabindex="-1" @click="isShowConfirmPassword = !isShowConfirmPassword">
              {{ isShowConfirmPassword ? 'visibility' : 'visibility_off' }}
            </v-icon>
          </template>
        </v-text-field>
      </v-hover>

      <v-layout>
        <v-checkbox
          v-model="formData.notification"
          :ripple="false"
          class="flex-0-0 mt-0 pt-0"
          hide-details
        />
        <span class="text-body-2">I want to receives updates from EOwn related to marketing tips and templates, product and feature updates, and promotions.</span>
      </v-layout>

      <v-btn
        large
        depressed
        type="submit"
        width="100%"
        class="mt-7 primary"
      >
        Sign Up
      </v-btn>

      <p class="mt-4 mb-0 text-term-privacy">
        By clicking the “Sign Up” button, you are creating an EOwn account, and you agree to <a href="https://eown.io/terms-and-conditions/" target="_blank" class="text-decoration-underline primary--text">EOwn’s Terms and Conditions</a> and <a href="/privacy-policy" target="_blank" class="text-decoration-underline primary--text">Privacy Policy</a>.
      </p>

      <v-layout class="my-5">
        <v-divider class="my-auto" />
        <span class="mx-2 text-subtitle-1 secondary--text">Or</span>
        <v-divider class="my-auto" />
      </v-layout>

      <v-btn
        large
        depressed
        width="100%"
        color="#CD5542"
        class="mb-3 white--text justify-start"
        @click="ggLogin"
      >
        <v-icon size="28" class="mr-4">
          mdi-google
        </v-icon>
        Continue with Google
      </v-btn>

      <v-btn
        large
        depressed
        width="100%"
        color="#415A93"
        class="white--text justify-start"
        @click="facebookLogin"
      >
        <v-icon size="28" class="mr-4">
          mdi-facebook
        </v-icon>
        Continue with Facebook
      </v-btn>
    </v-form>
  </v-layout>
</template>

<script>
import { pick } from 'lodash'
import { popup } from '@/utils/index'
import recaptchaMixin from '@/mixins/recaptcha'

export default {
  mixins: [recaptchaMixin],

  layout: 'authentication',

  auth: false,

  middleware({ store, redirect, $helpers, query }) {
    const auth = $helpers.get(store, 'state.auth', {})
    const user = $helpers.get(auth, 'user')
    const isLoggedIn = $helpers.get(auth, 'loggedIn')
    if (isLoggedIn && $helpers.get(user, 'role') !== 'guest') {
      redirect(query.redirect || '/')
    }
  },

  data: () => ({
    isShowPassword: false,
    isShowConfirmPassword: false,
    formData: {}
  }),
  head() {
    return {
      title: 'Sign Up',
      meta: [
        {
          hid: 'og:title',
          property: 'og:title',
          content: 'Sign Up Stream Studio'
        },
        {
          hid: 'twitter:title',
          name: 'twitter:title',
          content: 'Sign Up Stream Studio'
        },
        {
          hid: 'apple-mobile-web-app-title',
          name: 'apple-mobile-web-app-title',
          property: 'apple-mobile-web-app-title',
          content: 'Sign Up Stream Studio'
        },
        {
          hid: 'og:site_name',
          name: 'og:site_name',
          property: 'og:site_name',
          content: 'Sign Up Stream Studio'
        }
      ]
    }
  },
  created() {
    this.formData.email = this.$helpers.get(this.$route, 'query.email', '')
  },

  mounted() {
    const redirectData = {}
    redirectData.path = this.$auth.$storage.getUniversal('redirect') || '/'
    if (Object.keys(this.$route.query).length) {
      redirectData.query = this.$route.query
    }
    this.$store.commit('setLoginRedirect', redirectData)
  },

  methods: {
    async signUp() {
      const ok = this.$refs.form.validate()
      if (!ok) { return }
      const token = await this.getRecaptchaToken('signup')
      try {
        const resp = await this.$axios.$post('/auth/signup', {
          ...pick(this.formData, ['email', 'password', 'notification']),
          recaptchaResponse: token
        })
        if (resp.status === 'success') {
          this.goLogin()
        }
        // await this.$auth.setUserToken(resp.token)
        // await this.$auth.fetchUser()
        this.$notify.success({
          title: 'Sign Up',
          message: 'Successfully! Please check your email to confirm your account'
        })
        this.$helpers.tractMixpanel('subscribe_free', 'new user')
        this.$helpers.tractMixpanel('sign_up_success ', 'sign up successfully')
      } catch (error) {
        this.$notify.error({
          title: 'Sign Up',
          message: this.$helpers.apiErrorMessage(error)
        })
      }
      await this.resetRecaptcha()
    },
    async ggLogin() {
      const res = await popup(this.$config.apiUrl + '/auth/google')
      // console.log('res', res)
      try {
        const data = await this.$axios.$get('/auth/google/callback?' + res.query)
        // console.log('data', data)
        // this.$auth.setUser(data.data.user)
        this.$auth.setUserToken(data.token)
        this.goLogin()
      } catch (error) {
        this.$notify.error({
          title: 'Login',
          message: this.$helpers.apiErrorMessage(error)
        })
      }
    },
    async facebookLogin() {
      const res = await popup(this.$config.apiUrl + '/auth/facebook', true)
      try {
        const data = await this.$axios.$get('/auth/facebook/callback?code=' + res.query.code)
        // console.log('data', data)
        // this.$auth.setUser(data.data.user)
        this.$auth.setUserToken(data.token)
        this.goLogin()
      } catch (error) {
        this.$notify.error({
          title: 'Login',
          message: this.$helpers.apiErrorMessage(error)
        })
      }
    },
    goLogin() {
      this.$router.push({
        path: '/login',
        query: this.$route.query
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.signup {
  margin-top: 160px;
  max-width: 570px;
  .text-term-privacy {
    font-size: 12px;
    line-height: 20px;
    letter-spacing: 0.004em;
    color: #6E6D7A;
  }
}
</style>
